<template>
  <div :class="{ disabled: disabled }">
    <div class="cropped-image-wrapper">
      <div class="cropped-image" @click.prevent="showFileChooser">
        <template v-if="cropImg">
          <img
            ref="cropImage"
            :src="cropImg"
            alt="........"
            :class="imgClasses"
            :style="sizeStyle"
          />
          <button
            v-if="!disabled"
            type="button"
            class="btn btn-xs btn-danger btn-circle btn-remove"
            :title="$I18n.t('image_inline_input.remove_title')"
            @click.stop.prevent="removeImage()"
          >
            <i class="fa fa-times"></i>
          </button>
        </template>
        <img
          v-else
          :src="noImageSrc"
          alt="........"
          class="crop-placeholder"
          :class="imgClasses"
          :style="sizeStyle"
          style="object-fit: cover;"
        />
        <button
          v-if="!disabled && canSave && modified"
          type="button"
          class="btn btn-xs btn-success btn-circle btn-save"
          :title="$I18n.t('image_inline_input.save_title')"
          @click.stop.prevent="$emit('save')"
        >
          <i class="fa fa-save"></i>
        </button>
        <div v-if="canSave && modified" class="label label-info label-modified">
          {{ $I18n.t('no_guardado') }}
        </div>
      </div>
    </div>

    <input
      type="hidden"
      :name="imageFieldInputName"
      :value="imageField"
      :disabled="disabled"
    >
    <input
      ref="input"
      type="file"
      accept="image/*"
      :name="imageInputFormName"
      :disabled="disabled"
      @change="setImage"
    />
    <input
      type="hidden"
      :name="deleteImageInputFormName"
      :value="deleteImage"
      :disabled="disabled"
    >

    <div v-if="showEditor" class="content">
      <div class="modal-mask">
        <div class="modal-wrapper">
          <section class="card">
            <div class="card-body">
              <div class="img-cropper">
                <vue-cropper
                  ref="cropper"
                  :aspect-ratio="aspectRatio"
                  :src="imgSrc"
                  :auto-crop-area="1"
                  :view-mode="0"
                />
              </div>
            </div>
            <div class="card-footer">
              <div>
                <button class="btn btn-default" @click.prevent="zoom(0.2)">
                  <i class="fa fa-search-plus"></i>
                </button>
                <button class="btn btn-default" @click.prevent="zoom(-0.2)">
                  <i class="fa fa-search-minus"></i>
                </button>
                <button class="btn btn-default" @click.prevent="rotate(90)">
                  <i class="fa fa-redo"></i>
                </button>
                <button class="btn btn-default" @click.prevent="rotate(-90)">
                  <i class="fa fa-undo"></i>
                </button>
                <button
                  ref="flipX"
                  class="btn btn-default"
                  @click.prevent="flipX"
                >
                  <i class="fa fa-arrows-h"></i>
                </button>
                <button
                  ref="flipY"
                  class="btn btn-default"
                  @click.prevent="flipY"
                >
                  <i class="fa fa-arrows-v"></i>
                </button>
                <button class="btn btn-danger" @click.prevent="reset">
                  <i class="fa fa-sync"></i>
                </button>
              </div>
              <div class="form-actions">
                <button
                  class="btn btn-default"
                  @click.prevent="showEditor = false"
                >
                  {{ $I18n.t('Cancelar') }}
                </button>
                <button class="btn btn-cifra" @click.prevent="guardar">
                  {{ $I18n.t('Aceptar') }}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/*
Componenete que pinta una imagen (de thumbnail cuadrado de 128x128) y al
hacer click seleccionamos una imagen y abre un modal para seleccionar el
cuadrado que queremos utilizar.
 */
import Compressor from 'compressorjs';
import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';
import NoImage from '../../../assets/images/no-image.png';

export default {
  components: {
    VueCropper,
  },
  props: {
    formNamespace: { type: String, default: undefined },
    imageField: { type: String, default: 'foto' },
    src: { type: String, default: '' },
    noImageSrc: { type: String, default: NoImage },
    deleting: { type: Boolean, default: false },
    canSave: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    width: { type: Number, default: 128 },
    height: { type: Number, default: 128 },
    // 'round', 'square', 'none'
    borderStyle: { type: String, default: 'round' },
    calidadImagen: { type: Number, default: 1 },
  },
  data() {
    return {
      imgSrc: '',
      cropImg: this.src,
      data: null,
      tipoMime: null,
      showEditor: false,
      modified: false,
      deleteImage: this.deleting,
    };
  },
  computed: {
    imageFieldInputName() {
      return this.aplicarNamespace('image_field');
    },
    imageInputFormName() {
      return this.aplicarNamespace(this.imageField);
    },
    deleteImageInputFormName() {
      return this.aplicarNamespace(`delete_${this.imageField}`);
    },
    aspectRatio() {
      return this.width / this.height;
    },
    sizeStyle() {
      // Si no ponemos el height entonces el navegador recalcula el alto de la imagen
      // y al modificarla, la pone borrosa
      return `width: ${this.width}px; height: ${this.height}px; max-height: ${this.height}px;`;
    },
    imgClasses() {
      let classes = 'img-responsive';
      if (this.borderStyle === 'round') {
        classes = `${classes} img-thumbnail img-circle`;
      } else if (this.borderStyle === 'square') {
        classes = `${classes} img-thumbnail`;
      }
      return classes;
    },
  },
  watch: {
    cropImg() {
      this.modified = true;
    },
    modified(value) {
      this.$emit('modified', value);
    },
  },
  mounted() {
    if (this.cropImg.startsWith('data')) {
      const img = this.$refs.cropImage;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        // naturalWidth es el tamaño real de la imágen independientemente
        // de que en la web siempre se muestre a un tamaño de 128x128
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        const context = canvas.getContext('2d');
        context.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          const resizedFile = new File([blob], 'foto.jpeg');
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(resizedFile);
          this.$refs.input.files = dataTransfer.files;
        }, 'image/jpeg', this.calidadImagen);
      };
    }
  },
  methods: {
    cropImage() {
      // get image data for post processing, e.g. upload or setting image src
      this.cropImg = this.$refs.cropper.getCroppedCanvas().toDataURL();
    },
    flipX() {
      const dom = this.$refs.flipX;
      let scale = dom.getAttribute('data-scale');
      scale = scale ? -scale : -1;
      this.$refs.cropper.scaleX(scale);
      dom.setAttribute('data-scale', scale);
    },
    flipY() {
      const dom = this.$refs.flipY;
      let scale = dom.getAttribute('data-scale');
      scale = scale ? -scale : -1;
      this.$refs.cropper.scaleY(scale);
      dom.setAttribute('data-scale', scale);
    },
    getCropBoxData() {
      this.data = JSON.stringify(this.$refs.cropper.getCropBoxData(), null, 4);
    },
    getData() {
      this.data = JSON.stringify(this.$refs.cropper.getData(), null, 4);
    },
    move(offsetX, offsetY) {
      this.$refs.cropper.move(offsetX, offsetY);
    },
    reset() {
      this.imgSrc = '';
      this.$refs.cropper.reset();
    },
    rotate(deg) {
      this.$refs.cropper.rotate(deg);
    },
    setCropBoxData() {
      if (!this.data) return;
      this.$refs.cropper.setCropBoxData(JSON.parse(this.data));
    },
    setData() {
      if (!this.data) return;
      this.$refs.cropper.setData(JSON.parse(this.data));
    },
    getImage() {
      return this.$refs.input.files[0];
    },
    setImage(e) {
      const file = e.target.files[0];
      if (file.type.indexOf('image/') === -1) {
        // TODO: Gestión de errores
        alert('Debe seleccionar un fichero de tipo imagen');
        return;
      }

      this.tipoMime = file.type;

      // hay que mostrarlo antes porque si no no existe cropper y no podemos asignarle la imagen
      this.showEditor = true;
      this._convertirImagen(file, (imageObjectUrl) => {
        this.imgSrc = imageObjectUrl;
        this.$refs.cropper.replace(imageObjectUrl);
      });
    },
    showFileChooser() {
      this.$refs.input.click();
    },
    zoom(percent) {
      this.$refs.cropper.relativeZoom(percent);
    },
    guardar() {
      this.cropImage();

      const tipoMime = this.tipoMime || 'image/jpeg';
      const extension = tipoMime.includes('/') ? tipoMime.split('/')[1] : 'jpeg';
      const nombreFichero = `foto.${extension}`;

      this.$refs.cropper.getCroppedCanvas().toBlob(
        (blob) => {
          const resizedFile = new File([blob], nombreFichero, { type: tipoMime });
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(resizedFile);
          this.$refs.input.files = dataTransfer.files;

          this.deleteImage = false;
          this.showEditor = false;
          this.reset();
        },
        tipoMime,
        this.calidadImagen,
      );
    },
    removeImage() {
      this.cropImg = '';
      this.$refs.input.value = '';
      this.deleteImage = true;
    },
    aplicarNamespace(campo) {
      if (this.formNamespace) {
        return `${this.formNamespace}[${campo}]`;
      }
      return campo;
    },
    // Si seleccionan una imagen muy grande, la transformamos antes de cargarla
    // en el selector cropper
    _convertirImagen(fileOrBlob, callback) {
      const compressor = new Compressor(fileOrBlob, {
        quality: this.calidadImagen,
        maxWidth: 768,
        maxHeight: 768,
        success: (result) => {
          const imageObjectUrl = URL.createObjectURL(result);
          callback(imageObjectUrl);
        },
        error(err) {
          // TODO: Gestión de errores ¿Emitimos evento?
          alert(err.message);
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
input[type="file"] {
  display: none;
}
.cropped-image-wrapper {
  text-align: center;
}
.cropped-image {
  position: relative;
  display: inline-block;

  img {
    // Para que los 128px sea los de la imagen sin contar el borde del img-thumbnail
    box-sizing: content-box;
    max-width: 100%;
  }
}
.card {
  max-width: 90%;
  max-height: 700px;
  margin: auto;
}
// css para que sea un modal
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, .5);
  display: table;
  transition: opacity .3s ease;
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}
.label-modified {
  position: absolute;
  right: 0;
  bottom: 5px;
}
.btn-remove {
  display: none;
  position: absolute;
  right: 0;
  top: 5px;
}
.btn-save {
  position: absolute;
  left: 0;
  bottom: 0;
}
.cropped-image {
  cursor: pointer;

  .disabled & {
    cursor: initial;
  }

  &:hover {
    .btn-edit,
    .btn-remove {
      &:not([disabled]) {
        display: block;
      }
    }
  }
}
</style>
